import {
  FeatureFlagContentValuesType,
  FeatureKeys,
} from "contexts/FeatureFlagContext";
import { ReactNode } from "react";
import { TicketSystemType } from "views/Findings/Constants";

export type IdNameType = {
  id: string | number;
  name: string;
};

export type LabelValueType = {
  value: string | number;
  label: string;
};

export type IntegrationCustomFieldTypes = {
  key?: string;
  name?: string;
  defaultVal?: string | number | string[] | number[] | Date | null;
  type?: string;
  dataType?: string | null;
  allowedValues?: Record<string, string | number>[];
  active?: boolean;
  isRequired?: boolean;
  level?: number;
  nestedFields?: IntegrationCustomFieldTypes;
  mappings?: GenericIntegrationTicketingProperties[];
  selectedValues?: IdNameType[];
};

export type ConfigMetaDataType = {
  issueTypeMetaData: { id: string; name: string };
  projectMetaData: { id: string; name: string };
};

export type GenericIntegrationTicketDefaultValues = {
  organisation?: string;
  formName?: string;
  organisationName?: string;
  id?: number;
  product?: string[];
  productNameId?: { [key: string]: string };
  subProductIds?: string[];
  subProduct?: string[];
  projectKey?: string;
  projectKeyId?: string | null;
  projectKeyNames?: [{ id: string; name: string }];
  issueType?: string | null;
  issueTypeId?: string | null;
  critical?: string;
  high?: string;
  medium?: string;
  low?: string;
  info?: string;
  defaultConfiguration?: boolean;
  ticketUnifiedMultipleTemplateId?: number;
  ticketUnifiedSingleTemplateId?: number;
  transitions?: string;
  labels?: string[];
  markResolved?: boolean;
  enabled?: boolean;
  properties?: GenericIntegrationTicketingProperties;
  loginConfigId?: number;
  url?: string;
  configurationType?: string;
  customFields?: IntegrationCustomFieldTypes[];
  resolvedStatus?: string;
  reopenStatus?: string;
  markReopen?: boolean;
  repo?: string;
  configMetaData?: ConfigMetaDataType;
};

export type GenericIntegrationTicketingProperties = {
  critical?: string;
  high?: string;
  medium?: string;
  low?: string;
  info?: string;
  // This index signature allows for indexing object of this type with dynamic additional properties with keys of type string and values of any type.
  [key: string]: any;
};

export const FRESHSERVICE_CONFIG_DEFAULT_VALUES: GenericIntegrationTicketDefaultValues =
  {
    product: ["all"],
    subProduct: ["all"],
    critical: "Highest",
    high: "High",
    medium: "Medium",
    low: "Low",
    info: "Lowest",
  };

export const CUSTOM_FILED_SUPPORTED_INTEGRATIONS = [
  {
    tool: "Jira",
  },
  {
    tool: "Service Now",
  },
  {
    tool: "FreshService",
  },
  {
    tool: "Zendesk",
  },
  {
    tool: "Asana",
  },
  {
    tool: "Archer",
  },
];

export const genericIntegrationTicketProductSubproductAppendAll = (
  list: any,
) => [{ label: "All", value: "all" }, ...(list || [])];

export const FR_CUSTOM_CATEGORY_FIELDS = ["sub_category", "item_category"];

export const TEMPLATE_SUPPORTED_INTEGRATIONS = [
  {
    tool: "Jira",
    featureFlagCheck: (featureFlags: any) =>
      !featureFlags[FeatureKeys.UNIFIED_TICKET_TEMPLATE],
  },
  {
    tool: "Azure Board",
    featureFlagCheck: (featureFlags: any) =>
      !featureFlags[FeatureKeys.UNIFIED_TICKET_TEMPLATE],
  },
  {
    tool: "FreshService",
    featureFlagCheck: (featureFlags: any) =>
      !featureFlags[FeatureKeys.UNIFIED_TICKET_TEMPLATE],
  },
];

export type GenericIntegrationTicketSystemListingProps = {
  loginConfigList?: any[];
  loading?: boolean;
  extraColumns?: any;
  getColumns?: (fields: any[]) => any[];
  ConfigMappingForm?: ReactNode;
  projectKeyColumnTitle?: string;
};

export const TOOLS_USE_PAGINATED_API = ["jira", "azure board", "service now"];

export const CUSTOM_FIELDS_RISK_RATING_MAPPING_KEYS = [
  "goodRiskScore",
  "fairRiskScore",
  "poorRiskScore",
  "veryPoorRiskScore",
];
export const CONFIG_UPDATE_WARNING_MESSAGE =
  "If You select a different Project Key or Issue Type. Please review the settings and mapping on this screen before saving to ensure issues in the new Project Key or Issue Type work as expected";

export const NULL_PROJECT_KEY_SUPPORTED_TICKETING_TOOLS = ["zendesk"];
export const UNIFIED_TICKET_TEMPLATE_SUPPORTED_TOOLS = [
  TicketSystemType.AZURE_BOARD,
  TicketSystemType.FRESH_SERVICE,
  TicketSystemType.JIRA,
  TicketSystemType.SERVICE_NOW,
  TicketSystemType.ZENDESK,
  TicketSystemType.ASANA,
];

export const CUSTOM_VARIABLES = [
  "finding.customField.<labelName>",
  "finding.tag.<key>",
  "project.tag.<key>",
];

export const CMDB_SUPPORTED_TOOLS = [
  {
    tool: "Jira",
    featureFlagCheck: (featureFlags: FeatureFlagContentValuesType) =>
      featureFlags[FeatureKeys.RBVM_ASSETS],
  },
  {
    tool: "Service Now",
    featureFlagCheck: (featureFlags: FeatureFlagContentValuesType) =>
      featureFlags[FeatureKeys.RBVM_ASSETS],
  },
];

export const CUSTOM_SEVERITY_MAPPING_SUPPORTED_TOOLS = [
  "jira",
  "azure board",
  "asana",
];

export const TICKETING_LABELS_UNSUPPORTED_TOOLS = [
  TicketSystemType.SERVICE_NOW,
  TicketSystemType.ARCHER,
  TicketSystemType.GITHUB_ISSUES,
];

export const CUSTOM_FIELDS_VARIABLE_FIELD_OPTIONS = {
  duedate: [
    { value: "custom-value", label: "Custom Value" },
    {
      label: "${finding.remediationDueDate}",
      value: "${finding.remediationDueDate}",
    },
    {
      label: "${finding.resolutionDueDate}",
      value: "${finding.resolutionDueDate}",
    },
    { label: "${finding.triageDueDate}", value: "${finding.triageDueDate}" },
  ],
};
